import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, X, Save, Clapperboard, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import Sidebar from '../components/Sidebar';
import UpgradeModal from '../components/UpgradeModal';
import { useProject } from '../context/ProjectContext';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useTranslation } from 'react-i18next';
import SectionCanvas from '../components/SectionCanvas';
import { devLevel, groupDevLevel } from '../lib/devLevel';
import { SCROLL_CANVAS } from '../lib/featureFlags';

/** A scene's substance = its actual prose, judged by the shared threshold. */
const sceneText = (scene) =>
  [
    scene.action || scene.motivation,
    scene.want_vs_need,
    scene.conflict || scene.obstacle,
    scene.turn || scene.change,
    scene.notes,
  ].filter(Boolean).join('\n');

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BEAT_LABELS = {
  opening_image:     'First Frame',
  setup:             'The World Before',
  theme_stated:      'The Core Truth',
  catalyst:          'The Spark',
  debate:            'The Crossroads',
  break_into_two:    'The Leap',
  b_story:           'The Heart Thread',
  fun_and_games:     'The Promise',
  midpoint:          'The Turning Tide',
  bad_guys_close_in: 'The Walls Closing',
  all_is_lost:       'The Breaking Point',
  dark_night:        'The Hollow',
  break_into_three:  'The Rising',
  finale:            'The Reckoning',
  final_image:       'The Last Frame',
  obstacle:          'The Obstacle',
  escalation:        'The Escalation',
};

const SPARK_SCENE_LIMIT = 3;

const EMPTY_FORM = {
  title: '',
  location: '',
  action: '',
  want_vs_need: '',
  conflict: '',
  turn: '',
  scene_function: '',
  polarity_start: '',
  polarity_end: '',
  notes: '',
};

const SCENE_FUNCTION_OPTIONS = ['Introduce', 'Escalate', 'Reveal', 'Confront', 'Resolve'];

export default function SceneBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { project } = useProject();
  const { t } = useTranslation();

  const beatKey = searchParams.get('beat_key') || null;
  // Use URL param first; fall back to active project context
  const plotProjectId = searchParams.get('plot_project_id') || project?.plotProjectId || null;
  const beatLabel = searchParams.get('beat_label') || (beatKey ? BEAT_LABELS[beatKey] : null);

  // If the plotProjectId matches the active workspace, show workspace sidebar
  const isInWorkspace = Boolean(project && plotProjectId && plotProjectId === project.plotProjectId);

  const [user, setUser] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [plotProjects, setPlotProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formPlotProjectId, setFormPlotProjectId] = useState(plotProjectId || '');
  const [formBeatKey, setFormBeatKey] = useState(beatKey || '');
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(false);

  const isSparkUser = user?.subscription_tier === 'spark' || (!user?.subscription_tier && !user?.is_pro);
  const sceneCount = scenes.length;
  const atLimit = isSparkUser && sceneCount >= SPARK_SCENE_LIMIT;

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (plotProjectId) params.set('plot_project_id', plotProjectId);
      if (beatKey) params.set('beat_key', beatKey);

      const [userRes, scenesRes, projectsRes] = await Promise.all([
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(`${API}/scenes?${params}`, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' }),
      ]);
      const userData = await userRes.json();
      const scenesData = await scenesRes.json();
      const projectsData = await projectsRes.json();

      setUser(userData);
      setScenes(Array.isArray(scenesData) ? scenesData : []);
      setPlotProjects(Array.isArray(projectsData) ? projectsData.filter(p => p.project_type === 'plot') : []);
    } catch (error) {
      console.error('Error fetching scenes:', error);
      toast.error('Failed to load scenes');
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    if (atLimit) {
      setUpgradeModal(true);
      return;
    }
    setEditingScene(null);
    setFormData(EMPTY_FORM);
    setFormPlotProjectId(plotProjectId || '');
    setFormBeatKey(beatKey || '');
    setShowForm(true);
  };

  const openEditForm = (scene) => {
    setEditingScene(scene);
    setFormData({
      title: scene.title || '',
      location: scene.location || '',
      action: scene.action || scene.motivation || '',
      want_vs_need: scene.want_vs_need || '',
      conflict: scene.conflict || scene.obstacle || '',
      turn: scene.turn || scene.change || '',
      scene_function: scene.scene_function || '',
      polarity_start: scene.polarity_start || '',
      polarity_end: scene.polarity_end || '',
      notes: scene.notes || '',
    });
    setFormPlotProjectId(scene.plot_project_id || '');
    setFormBeatKey(scene.beat_key || '');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingScene(null);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Scene title is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...formData,
        plot_project_id: formPlotProjectId || null,
        beat_key: formBeatKey || null,
      };
      const url = editingScene
        ? `${API}/scenes/${editingScene.scene_id}`
        : `${API}/scenes`;
      const method = editingScene ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.status === 403) {
        closeForm();
        setUpgradeModal(true);
        return;
      }
      if (!response.ok) throw new Error('Failed to save scene');

      const saved = await response.json();
      if (editingScene) {
        setScenes(prev => prev.map(s => s.scene_id === saved.scene_id ? saved : s));
        toast.success('Scene updated');
      } else {
        setScenes(prev => [...prev, saved]);
        toast.success('Scene created');
      }
      closeForm();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save scene');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scene) => {
    try {
      const response = await authFetch(`${API}/scenes/${scene.scene_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete');
      setScenes(prev => prev.filter(s => s.scene_id !== scene.scene_id));
      toast.success('Scene deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete scene');
    }
  };

  const backTarget = isInWorkspace
    ? `/project/${plotProjectId}?tab=scenes`
    : plotProjectId
    ? `/plot/${plotProjectId}/level/1`
    : '/dashboard?type=plot';

  // Translated BMOC fields (labels/placeholders change by language)
  const bmocFields = [
    { key: 'action',       label: t('scene_actionLabel'),      placeholder: t('scene_actionPh') },
    { key: 'want_vs_need', label: t('scene_wantVsNeedLabel'),  placeholder: t('scene_wantVsNeedPh') },
    { key: 'conflict',     label: t('scene_conflictLabel'),    placeholder: t('scene_conflictPh') },
    { key: 'turn',         label: t('scene_turnLabel'),        placeholder: t('scene_turnPh') },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <div className="noise-overlay" />
      <Sidebar
        userTier={user?.subscription_tier}
        projectId={isInWorkspace ? plotProjectId : undefined}
        projectTitle={isInWorkspace ? project?.projectTitle : undefined}
      />

      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Header */}
        <header className="glassmorphism sticky top-0 z-40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(backTarget)}
              data-testid="back-btn"
              className="rounded-none font-mono uppercase tracking-widest text-xs"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isInWorkspace ? t('nav_backToWorkspace') : plotProjectId ? t('nav_backToPlot') : t('nav_backToDashboard')}
            </Button>
            <div className="font-mono text-sm text-muted-foreground">
              {beatLabel ? (
                <span>
                  Scenes for <span className="text-foreground">{beatLabel}</span>
                </span>
              ) : (
                'Scene Builder'
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          {/* Page heading */}
          <div className="mb-10 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Clapperboard className="w-8 h-8 text-primary" />
                <h1 className="font-mono text-2xl md:text-3xl font-semibold tracking-tight">
                  {t('scene_builder')}
                </h1>
              </div>
              {beatLabel && (
                <p className="text-muted-foreground font-mono text-sm">
                  {t('scene_beatPrefix')}<span className="text-primary">{beatLabel}</span>
                </p>
              )}
            </div>

            <div className="flex flex-col items-end gap-2">
              {isSparkUser && (
                <span className="font-mono text-xs text-muted-foreground">
                  {sceneCount} / {SPARK_SCENE_LIMIT} scenes
                </span>
              )}
              <Button
                onClick={openAddForm}
                data-testid="new-scene-btn"
                className="rounded-none font-mono uppercase tracking-widest text-xs px-5 py-3 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('scene_newScene')}
              </Button>
            </div>
          </div>

          {/* Scene list */}
          {scenes.length === 0 ? (
            <div
              className="border border-dashed border-border/40 p-16 text-center"
              data-testid="empty-state"
            >
              <Clapperboard className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="font-mono text-muted-foreground text-sm uppercase tracking-widest">
                {t('scene_noScenes')}
              </p>
              <p className="text-muted-foreground text-sm mt-2">
                {beatLabel
                  ? t('scene_noScenesBeat', { beat: beatLabel })
                  : t('scene_noScenesDesc')}
              </p>
              <Button
                onClick={openAddForm}
                className="mt-6 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('scene_addFirst')}
              </Button>
            </div>
          ) : (() => {
              const renderSceneCard = (scene, idx) => {
                const isExpanded = expandedId === scene.scene_id;
                const beatName = scene.beat_key ? BEAT_LABELS[scene.beat_key] : null;
                const project = plotProjects.find(p => p.project_id === scene.plot_project_id);

                return (
                  <div
                    key={scene.scene_id}
                    className="bg-card border border-border/40 transition-all"
                    data-testid={`scene-card-${scene.scene_id}`}
                  >
                    {/* Card header row */}
                    <div
                      className="flex items-center justify-between p-5 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : scene.scene_id)}
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-xs text-muted-foreground/50 w-6">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <div>
                          <div className="font-mono font-semibold">{scene.title}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {beatName && (
                              <span className="text-[10px] font-mono uppercase tracking-widest text-primary border border-primary/30 px-1.5 py-0.5">
                                {beatName}
                              </span>
                            )}
                            {scene.location && (
                              <span className="text-xs font-mono text-muted-foreground">
                                {scene.location}
                              </span>
                            )}
                            {scene.polarity_start && scene.polarity_end && (
                              <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                                {scene.polarity_start}
                                <ArrowRight className="w-3 h-3" />
                                {scene.polarity_end}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditForm(scene); }}
                          data-testid={`edit-scene-${scene.scene_id}`}
                          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(scene); }}
                          data-testid={`delete-scene-${scene.scene_id}`}
                          className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    {/* Expanded BMOC detail */}
                    {isExpanded && (
                      <div className="border-t border-border/40 px-5 pb-5 pt-4 space-y-4">
                        {project && (
                          <p className="text-xs font-mono text-muted-foreground">
                            Project: <span className="text-foreground">{project.title}</span>
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                            { label: t('scene_action_card'),       value: scene.action      || scene.motivation },
                            { label: t('scene_wantVsNeed_card'),   value: scene.want_vs_need },
                            { label: t('scene_conflict_card'),     value: scene.conflict    || scene.obstacle },
                            { label: t('scene_turn_card'),         value: scene.turn        || scene.change },
                          ].map(({ label, value }) => (
                            <div key={label}>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                                {label}
                              </div>
                              <div className="text-sm text-foreground/80">
                                {value || <span className="text-muted-foreground/40 italic">—</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {scene.scene_function && (
                          <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                              {t('scene_function_card')}
                            </div>
                            <div className="text-sm text-foreground/80">{scene.scene_function}</div>
                          </div>
                        )}
                        {(scene.polarity_start || scene.polarity_end) && (
                          <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                              {t('scene_polarityShift')}
                            </div>
                            <div className="flex items-center gap-2 font-mono text-sm">
                              <span className="text-green-400">{scene.polarity_start || '—'}</span>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <span className="text-red-400">{scene.polarity_end || '—'}</span>
                            </div>
                          </div>
                        )}
                        {scene.notes && (
                          <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                              Notes
                            </div>
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap">{scene.notes}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              if (!SCROLL_CANVAS) {
                return (
                  <div className="space-y-3" data-testid="scene-list">
                    {scenes.map((scene, idx) => renderSceneCard(scene, idx))}
                  </div>
                );
              }

              // Canvas mode: scenes as sections grouped by beat, with the
              // shared rail — depth dots (scene substance via devLevel),
              // ribbon, and per-beat milestone marks. Editing keeps the
              // proven modal; the canvas is the navigation + depth layer.
              const order = new Map(scenes.map((s, i) => [s.scene_id, i]));
              const byBeat = new Map();
              scenes.forEach((s) => {
                const key = s.beat_key && BEAT_LABELS[s.beat_key] ? s.beat_key : '__unassigned__';
                if (!byBeat.has(key)) byBeat.set(key, []);
                byBeat.get(key).push(s);
              });
              const beatOrder = [...Object.keys(BEAT_LABELS), '__unassigned__'];
              const groups = beatOrder
                .filter((key) => byBeat.has(key))
                .map((key) => {
                  const group = byBeat.get(key);
                  const isBeat = key !== '__unassigned__';
                  return {
                    id: `beat-${key}`,
                    title: isBeat ? BEAT_LABELS[key] : t('scene_unassigned', 'Unassigned'),
                    dev: groupDevLevel(group.map(sceneText)),
                    milestoneText: isBeat
                      ? t('milestone_developed', { name: BEAT_LABELS[key] })
                      : null,
                    sections: group.map((scene) => ({
                      id: scene.scene_id,
                      label: scene.title,
                      dev: devLevel(sceneText(scene)),
                      render: () => renderSceneCard(scene, order.get(scene.scene_id)),
                    })),
                  };
                });
              return (
                <div data-testid="scene-list">
                  <SectionCanvas groups={groups} />
                </div>
              );
            })()}
        </main>
      </div>

      {/* Scene Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="bg-card border border-border/60 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border/40">
              <h2 className="font-mono text-sm uppercase tracking-widest">
                {editingScene ? t('scene_editScene') : t('scene_newScene')}
              </h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Title — auto-expands; scene slugs run long ("EXT. NAIROBI / MATHARE — NIGHT — JOSEPH FINDS THE LETTER") */}
              <div className="space-y-2">
                <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {t('scene_titleRequired')}
                </label>
                <AutoExpandTextarea
                  value={formData.title || ''}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('scene_titlePh')}
                  data-testid="scene-title-input"
                  className="rounded-none font-mono bg-background border-input focus:ring-2 focus:ring-primary focus:border-transparent min-h-[60px]"
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {t('scene_locationLabel')}
                </label>
                <AutoExpandTextarea
                  value={formData.location || ''}
                  onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder={t('scene_locationPh')}
                  data-testid="scene-location-input"
                  className="rounded-none font-mono bg-background border-input focus:ring-2 focus:ring-primary focus:border-transparent min-h-[60px]"
                />
              </div>

              {/* Plot project + beat */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    {t('scene_plotProjectLabel')}
                  </label>
                  <select
                    value={formPlotProjectId}
                    onChange={e => setFormPlotProjectId(e.target.value)}
                    data-testid="scene-project-select"
                    className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">{t('scene_standalone')}</option>
                    {plotProjects.map(p => (
                      <option key={p.project_id} value={p.project_id}>{p.title}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    {t('scene_beatLabel')}
                  </label>
                  <select
                    value={formBeatKey}
                    onChange={e => setFormBeatKey(e.target.value)}
                    data-testid="scene-beat-select"
                    className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">{t('scene_none')}</option>
                    {Object.entries(BEAT_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* BMOC */}
              <div className="space-y-1">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  {t('scene_bmoc')}
                </div>
                <div className="space-y-3">
                  {bmocFields.map(({ key, label, placeholder }) => (
                    <div key={key} className="space-y-1">
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                        {label}
                      </label>
                      <AutoExpandTextarea
                        value={formData[key] || ''}
                        onChange={e => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={placeholder}
                        data-testid={`scene-${key}`}
                        className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/40 min-h-[60px] text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Scene Function */}
              <div className="space-y-2">
                <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {t('scene_functionLabel')}
                </label>
                <select
                  value={formData.scene_function}
                  onChange={e => setFormData(prev => ({ ...prev, scene_function: e.target.value }))}
                  data-testid="scene-function-select"
                  className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">{t('scene_fnSelectPh')}</option>
                  <option value="Introduce">{t('scene_fnIntroduce')}</option>
                  <option value="Escalate">{t('scene_fnEscalate')}</option>
                  <option value="Reveal">{t('scene_fnReveal')}</option>
                  <option value="Confront">{t('scene_fnConfront')}</option>
                  <option value="Resolve">{t('scene_fnResolve')}</option>
                </select>
              </div>

              {/* Polarity Shift */}
              <div className="space-y-2">
                <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {t('scene_polarityShift')}
                </label>
                <div className="flex items-start gap-3">
                  <AutoExpandTextarea
                    value={formData.polarity_start || ''}
                    onChange={e => setFormData(prev => ({ ...prev, polarity_start: e.target.value }))}
                    placeholder={t('scene_polarityStartPh')}
                    data-testid="scene-polarity-start"
                    className="rounded-none font-mono bg-background border-input focus:ring-2 focus:ring-primary focus:border-transparent min-h-[60px] flex-1"
                  />
                  <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-5" />
                  <AutoExpandTextarea
                    value={formData.polarity_end || ''}
                    onChange={e => setFormData(prev => ({ ...prev, polarity_end: e.target.value }))}
                    placeholder={t('scene_polarityEndPh')}
                    data-testid="scene-polarity-end"
                    className="rounded-none font-mono bg-background border-input focus:ring-2 focus:ring-primary focus:border-transparent min-h-[60px] flex-1"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {t('scene_notes')}
                </label>
                <AutoExpandTextarea
                  value={formData.notes || ''}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder={t('scene_notesPh')}
                  data-testid="scene-notes-input"
                  className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/40 min-h-[80px] text-sm"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-6 pb-5 flex items-center justify-between gap-3">
              <button
                onClick={closeForm}
                className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-scene-btn"
                className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? t('saving') : t('scene_saveScene')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {upgradeModal && (
        <UpgradeModal
          title="Every great film lives in its scenes."
          description="You've used all 3 scenes on the Spark plan. Upgrade to Indie to build unlimited scenes across all your story beats."
          benefits={[
            'Unlimited scenes on Indie and above',
            'Full B.M.O.C breakdown per scene',
            'Polarity shift tracking',
            'Link scenes to any plot beat',
          ]}
          ctaText="Unlock Unlimited Scenes"
          onClose={() => setUpgradeModal(false)}
        />
      )}
    </div>
  );
}
